import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  LinearProgress,
  Alert,
  Chip,
  Paper,
  Card,
  CardMedia,
  CardContent
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Description,
  Image,
  PictureAsPdf,
  Close
} from '@mui/icons-material';
import apiClient from '../config/axios';

const ModalFileSelect = ({ open, onClose, userId, onFilesUploaded }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef();

  const allowedTypes = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png'
  ];

  const getFileIcon = (fileType) => {
    if (fileType === 'application/pdf') return <PictureAsPdf color="error" />;
    if (fileType.startsWith('image/')) return <Image color="primary" />;
    return <Description color="action" />;
  };

  const getFileTypeLabel = (fileType) => {
    if (fileType === 'application/pdf') return 'PDF';
    if (fileType === 'image/jpeg' || fileType === 'image/jpg') return 'JPG';
    if (fileType === 'image/png') return 'PNG';
    return 'File';
  };

  const isImageFile = (fileType) => {
    return fileType.startsWith('image/');
  };

  const generatePreview = (file) => {
    return new Promise((resolve) => {
      if (isImageFile(file.type)) {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve(e.target.result);
        };
        reader.readAsDataURL(file);
      } else {
        resolve(null);
      }
    });
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        setError(`Tipo di file non supportato: ${file.name}`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setError(`File troppo grande: ${file.name} (max 10MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      setError('');
      
      // Genera anteprime per le immagini
      const newPreviews = {};
      for (const file of validFiles) {
        if (isImageFile(file.type)) {
          const preview = await generatePreview(file);
          newPreviews[file.name] = preview;
        }
      }
      setFilePreviews(prev => ({ ...prev, ...newPreviews }));
    }
  };

  const handleRemoveFile = (index) => {
    const fileToRemove = selectedFiles[index];
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    
    // Rimuovi anche l'anteprima se esiste
    if (fileToRemove && filePreviews[fileToRemove.name]) {
      setFilePreviews(prev => {
        const newPreviews = { ...prev };
        delete newPreviews[fileToRemove.name];
        return newPreviews;
      });
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Seleziona almeno un file da caricare');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');
    setSuccess('');

    try {
      const uploadPromises = selectedFiles.map(async (file, index) => {
        const formData = new FormData();
        formData.append('file', file);
        if (userId) {
          formData.append('userId', userId);
        }

        const response = await apiClient.post('/api/files/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        });

        return response.data;
      });

      const results = await Promise.all(uploadPromises);
      
      setSuccess(`${results.length} file caricati con successo!`);
      setSelectedFiles([]);
      
      if (onFilesUploaded) {
        onFilesUploaded(results);
      }

      // Reset progress after a short delay
      setTimeout(() => {
        setUploadProgress(0);
      }, 1000);

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.message || 'Errore durante il caricamento dei file');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setFilePreviews({});
    setError('');
    setSuccess('');
    setUploadProgress(0);
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Carica File</Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Tipi di file supportati: PDF, JPG, PNG (max 10MB per file)
          </Typography>
          
          <input
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
            ref={fileInputRef}
            onChange={handleFileSelect}
          />
          
          <Button
            variant="outlined"
            startIcon={<CloudUpload />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            fullWidth
            sx={{ mb: 2 }}
          >
            Seleziona File
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {uploading && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Caricamento in corso... {uploadProgress}%
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}

        {selectedFiles.length > 0 && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              File selezionati ({selectedFiles.length}):
            </Typography>
            <List dense>
              {selectedFiles.map((file, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                    {getFileIcon(file.type)}
                  </Box>
                  <ListItemText
                    primary={file.name}
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label={getFileTypeLabel(file.type)} 
                          size="small" 
                          variant="outlined" 
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(file.size)}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleRemoveFile(index)}
                      disabled={uploading}
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {/* Anteprima delle immagini */}
        {Object.keys(filePreviews).length > 0 && (
          <Paper sx={{ p: 2, mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Anteprima Immagini:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {selectedFiles
                .filter(file => isImageFile(file.type))
                .map((file, index) => (
                  <Card key={index} sx={{ width: 200, maxWidth: '100%' }}>
                    <CardMedia
                      component="img"
                      height="140"
                      image={filePreviews[file.name]}
                      alt={file.name}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent sx={{ p: 1 }}>
                      <Typography variant="caption" noWrap>
                        {file.name}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
            </Box>
          </Paper>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Annulla
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={selectedFiles.length === 0 || uploading}
          startIcon={<CloudUpload />}
        >
          {uploading ? 'Caricamento...' : 'Carica File'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalFileSelect; 